import { useEffect, useState, useMemo } from "react";
import { Table, Badge, Spinner, Button } from "reactstrap";
import { saveAs } from "file-saver";
import { getVacancyLeaseExpiryReport, exportVacancyLeaseExpiryExcel } from "../TenantForm/Helpers/api";

const STATUS = {
  VACANT: { color: "warning", label: "Vacant" },
  EXPIRED: { color: "danger", label: "Expired" },
  EXPIRING_30: { color: "danger", label: "Expiring ≤ 30 days" },
  EXPIRING_60: { color: "warning", label: "Expiring 31–60 days" },
  EXPIRING_90: { color: "info", label: "Expiring 61–90 days" },
  ACTIVE: { color: "success", label: "Active (> 90 days)" },
  NO_END_DATE: { color: "secondary", label: "No lease end date" },
};

const FILTERS = [
  { value: "ALL", label: "All units" },
  { value: "VACANT", label: "Vacant only" },
  { value: "EXPIRING_30", label: "Expiring ≤ 30 days" },
  { value: "EXPIRING_60", label: "Expiring 31–60 days" },
  { value: "EXPIRING_90", label: "Expiring 61–90 days" },
  { value: "EXPIRED", label: "Expired" },
];

const ROW_CLASS = {
  VACANT: "table-warning",
  EXPIRED: "table-danger",
  EXPIRING_30: "table-danger",
};

const VacancyLeaseExpiryTab = ({ masterSheetData }) => {
  const mastersheetId = masterSheetData?._id;
  const [report, setReport] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [statusFilter, setStatusFilter] = useState("ALL");
  const [downloading, setDownloading] = useState(false);

  useEffect(() => {
    let active = true;
    if (!mastersheetId) {
      setReport(null);
      return;
    }
    setLoading(true);
    setError(null);
    getVacancyLeaseExpiryReport(mastersheetId)
      .then((resp) => {
        if (!active) return;
        if (resp?.success) setReport(resp.data);
        else setError(resp?.message || "Failed to load report");
      })
      .catch((e) => {
        if (active) setError(e?.response?.data?.message || "Failed to load report");
      })
      .finally(() => {
        if (active) setLoading(false);
      });
    return () => {
      active = false;
    };
  }, [mastersheetId]);

  const visibleRows = useMemo(() => {
    if (!report?.rows) return [];
    return statusFilter === "ALL" ? report.rows : report.rows.filter((r) => r.status === statusFilter);
  }, [report, statusFilter]);

  const handleDownload = async () => {
    if (!mastersheetId) return;
    setDownloading(true);
    try {
      const blob = await exportVacancyLeaseExpiryExcel(mastersheetId);
      const safe = (report?.building_name || "report").replace(/[^a-z0-9_-]+/gi, "_");
      saveAs(blob, `Vacancy-LeaseExpiry_${safe}.xlsx`);
    } catch (e) {
      setError("Failed to download Excel.");
    } finally {
      setDownloading(false);
    }
  };

  if (loading)
    return (
      <div className="text-center p-4">
        <Spinner color="primary" /> Building report…
      </div>
    );
  if (error) return <p className="text-danger text-center">{error}</p>;
  if (!report) return <p className="text-muted text-center">No data available.</p>;

  const t = report.totals;

  return (
    <div>
      <div className="d-flex flex-wrap gap-3 mb-3">
        <div className="border rounded p-2 px-3">
          <div className="small text-muted">Total units</div>
          <strong>{t.unitsTotal}</strong>
        </div>
        <div className="border rounded p-2 px-3" style={{ background: "#fff3cd" }}>
          <div className="small text-muted">Vacant units</div>
          <strong>{t.vacant}</strong>
        </div>
        <div className="border rounded p-2 px-3" style={{ background: "#fdeaea" }}>
          <div className="small text-muted">Expiring in 30 days</div>
          <strong>{t.expiring30}</strong>
        </div>
        <div className="border rounded p-2 px-3">
          <div className="small text-muted">Expiring in 60 days</div>
          <strong>{t.expiring60}</strong>
        </div>
        <div className="border rounded p-2 px-3">
          <div className="small text-muted">Expiring in 90 days</div>
          <strong>{t.expiring90}</strong>
        </div>
        <div className="border rounded p-2 px-3" style={{ background: "#fdeaea" }}>
          <div className="small text-muted">Already expired</div>
          <strong>{t.expired}</strong>
        </div>
        <div className="border rounded p-2 px-3">
          <div className="small text-muted">Active (&gt; 90 days)</div>
          <strong>{t.active}</strong>
        </div>
        <div className="border rounded p-2 px-3">
          <div className="small text-muted">No lease end date</div>
          <strong>{t.noEndDate}</strong>
        </div>
      </div>

      <div className="d-flex align-items-center mb-2">
        <select
          className="form-select form-select-sm w-auto"
          value={statusFilter}
          onChange={(e) => setStatusFilter(e.target.value)}
          aria-label="Filter by status"
        >
          {FILTERS.map((f) => (
            <option key={f.value} value={f.value}>
              {f.label}
            </option>
          ))}
        </select>
        <Button color="success" className="ms-auto" onClick={handleDownload} disabled={downloading || !mastersheetId}>
          {downloading ? <Spinner size="sm" /> : "Download Excel"}
        </Button>
      </div>

      <Table hover responsive className="table-sm">
        <thead className="table-light">
          <tr>
            <th>Unit #</th>
            <th>Tenant</th>
            <th>Lease Start</th>
            <th>Lease End</th>
            <th className="text-end">Days Left</th>
            <th>Status</th>
          </tr>
        </thead>
        <tbody>
          {visibleRows.length ? (
            visibleRows.map((r, i) => (
              <tr key={`${r.unitNo}-${i}`} className={ROW_CLASS[r.status] || ""}>
                <td>
                  <strong>{r.unitNo}</strong>
                </td>
                <td>{r.tenant}</td>
                <td>{r.leaseStart || "—"}</td>
                <td>{r.leaseEnd || "—"}</td>
                <td className="text-end">{r.daysUntilExpiry === null ? "—" : r.daysUntilExpiry}</td>
                <td>
                  <Badge color={STATUS[r.status]?.color ?? "secondary"}>{STATUS[r.status]?.label ?? r.status}</Badge>
                </td>
              </tr>
            ))
          ) : (
            <tr>
              <td colSpan={6} className="text-center text-muted">
                No units to display.
              </td>
            </tr>
          )}
        </tbody>
      </Table>
    </div>
  );
};

export { VacancyLeaseExpiryTab };
